import type { ReactNode } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { RevealGroup, HEADING } from "@/components/site/Reveal";

type PageLayoutProps = {
  title: string;
  kicker?: string;
  intro?: string;
  children: ReactNode;
};

export function PageLayout({ title, kicker, intro, children }: PageLayoutProps) {
  return (
    <div>
      <SiteHeader />

      <section className="relative border-b border-border bg-background px-6 pb-16 pt-36 md:px-10 md:pb-20 md:pt-44">
        <div className="mx-auto max-w-[1400px]">
          <RevealGroup stagger={70} maxStagger={200}>
            {kicker && (
              <div className="flex items-center gap-4 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-muted-foreground">
                <span className="text-sun">01</span>
                <span className="h-px w-10 bg-border" />
                <span>{kicker}</span>
              </div>
            )}
            <h1 className={`mt-6 ${HEADING.h1} text-foreground`}>{title}</h1>
            {intro && <p className={`mt-6 ${HEADING.lead}`}>{intro}</p>}
          </RevealGroup>
        </div>
      </section>

      <main>{children}</main>

      <SiteFooter />
    </div>
  );
}

type PageSectionTheme = "default" | "surface";

type PageSectionProps = {
  theme?: PageSectionTheme;
  label?: string;
  title?: string;
  children: ReactNode;
  className?: string;
};

const THEME_CLASSES: Record<PageSectionTheme, string> = {
  default: "",
  surface: "bg-surface",
};

export function PageSection({ theme = "default", label, title, children, className = "" }: PageSectionProps) {
  return (
    <section
      className={`border-b border-border px-6 py-24 md:px-10 md:py-32 ${THEME_CLASSES[theme]} ${className}`.trim()}
    >
      <div className="mx-auto max-w-[1400px]">
        {(label || title) && (
          <div className="mb-12 md:mb-16">
            <RevealGroup stagger={70} maxStagger={160}>
              {label && (
                <div className="flex items-center gap-4 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-muted-foreground">
                  <span className="text-sun">{label}</span>
                  <span className="h-px w-10 bg-border" />
                </div>
              )}
              {title && <h2 className={`mt-6 ${HEADING.h2}`}>{title}</h2>}
            </RevealGroup>
          </div>
        )}
        {children}
      </div>
    </section>
  );
}
